import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import {
  PaginatedBudgetProposals,
  Proposal,
  ProposalDetails,
} from 'src/assets/types/interface/proposal.interface';
import { map, Observable, tap } from 'rxjs';
import { TimedRequestCache } from 'src/app/services/timed-request-cache';
import { Budget } from 'src/assets/types/interface/budget.interface';
import { AiBudgetGenerationStatusResponse } from 'src/assets/types/interface/ai-budget-generation-status.interface';

@Injectable({
  providedIn: 'root',
})
export class BudgetProviderService {
  private readonly apiUrl = environment.WO_API_URL;
  private readonly proposalDetailsCache = new TimedRequestCache<Proposal>();
  private readonly versionDetailsCache = new TimedRequestCache<Budget>();
  private readonly proposalBudgetDetailsCache =
    new TimedRequestCache<ProposalDetails>();

  constructor(private http: HttpClient) {}

  getAllProposalByTenant() {
    const access_token = localStorage.getItem('access_token') ?? '';
    return this.http.get<Proposal[]>(`${this.apiUrl}/budget/provider/`, {
      headers: {
        Authorization: `Bearer ${access_token}`,
      },
    });
  }

  getAllProposalsByPage(
    take: number,
    page: number,
    filters?: {
      date?: string | null;
      dateFrom?: string;
      dateTo?: string;
      number?: string;
      statusList?: string[];
    },
  ) {
    const access_token = localStorage.getItem('access_token') ?? '';
    let params = new HttpParams();

    if (filters?.date) {
      params = params.set('date', filters.date);
    }
    if (filters?.dateFrom) params = params.set('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params = params.set('dateTo', filters.dateTo);

    if (filters?.number) {
      params = params.set('number', filters.number);
    }

    const validStatuses = (filters?.statusList || []).filter(
      (status) => status.trim() !== '',
    );

    if (validStatuses.length > 0) {
      validStatuses.forEach((status) => {
        params = params.append('status', status);
      });
    }

    return this.http.get<PaginatedBudgetProposals>(
      `${this.apiUrl}/budget/provider/limit/${take}/page/${page}`,
      {
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
        params,
      },
    );
  }

  getProposalVersions(proposalUuid: string): Observable<Proposal> {
    const access_token = localStorage.getItem('access_token') ?? '';
    return this.proposalDetailsCache.get(
      `${access_token}:${proposalUuid}`,
      () =>
        this.http.get<Proposal>(
          `${this.apiUrl}/budget/provider/versions/${proposalUuid}`,
          {
            headers: {
              Authorization: `Bearer ${access_token}`,
            },
          },
        ),
    );
  }

  getDraftVersionWithAI(proposalUuid: string): Observable<Budget> {
    const access_token = localStorage.getItem('access_token') ?? '';
    return this.http.get<Budget>(
      `${this.apiUrl}/budget/provider/${proposalUuid}/draft-version/true`,
      {
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
      },
    );
  }

  getDraftVersionWithoutAI(proposalUuid: string): Observable<Budget> {
    const access_token = localStorage.getItem('access_token') ?? '';
    return this.http.get<Budget>(
      `${this.apiUrl}/budget/provider/${proposalUuid}/draft-version/false`,
      {
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
      },
    );
  }

  requestAiBudgetGeneration(
    proposalUuid: string,
  ): Observable<AiBudgetGenerationStatusResponse> {
    const access_token = localStorage.getItem('access_token') ?? '';
    return this.invalidateDetailCachesAfter(
      this.http.post<AiBudgetGenerationStatusResponse>(
        `${this.apiUrl}/budget/provider/${proposalUuid}/generate-ai-budget`,
        {},
        {
          headers: {
            Authorization: `Bearer ${access_token}`,
          },
        },
      ),
    );
  }

  getAiBudgetGenerationStatus(
    proposalUuid: string,
  ): Observable<AiBudgetGenerationStatusResponse> {
    const access_token = localStorage.getItem('access_token') ?? '';
    return this.http.get<AiBudgetGenerationStatusResponse>(
      `${this.apiUrl}/budget/provider/${proposalUuid}/generate-ai-budget/status`,
      {
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
      },
    );
  }

  getVersionDetails(budgetUuid: string): Observable<Budget> {
    const access_token = localStorage.getItem('access_token') ?? '';
    return this.versionDetailsCache.get(`${access_token}:${budgetUuid}`, () =>
      this.http.get<Budget>(
        `${this.apiUrl}/budget/provider/details/${budgetUuid}`,
        {
          headers: {
            Authorization: `Bearer ${access_token}`,
          },
        },
      ),
    );
  }

  getProposalDetails(
    proposalUuid: string,
    budgetUuid: string,
  ): Observable<ProposalDetails> {
    const access_token = localStorage.getItem('access_token') ?? '';
    return this.proposalBudgetDetailsCache
      .get(`${access_token}:${proposalUuid}`, () =>
        this.http.get<ProposalDetails>(
          `${this.apiUrl}/budget/provider/${proposalUuid}/details/latest`,
          { headers: { Authorization: `Bearer ${access_token}` } },
        ),
      )
      .pipe(
        map((response) => ({
          ...response,
          selectedBudget:
            budgetUuid === 'latest'
              ? response.selectedBudget
              : ((response.budgets ?? []).find(
                  (budget) => budget.uuid === budgetUuid,
                ) ?? response.selectedBudget),
        })),
      );
  }

  createProposalVersion(data: any, proposalUuid: string): Observable<Budget> {
    const access_token = localStorage.getItem('access_token') ?? '';
    return this.invalidateDetailCachesAfter(
      this.http.post<Budget>(
        `${this.apiUrl}/budget/provider/${proposalUuid}/draft`,
        data,
        {
          headers: {
            Authorization: `Bearer ${access_token}`,
          },
        },
      ),
    );
  }

  submitVersion(data: any, budgetUuid: string) {
    const access_token = localStorage.getItem('access_token') ?? '';
    return this.invalidateDetailCachesAfter(
      this.http.post(
        `${this.apiUrl}/budget/provider/${budgetUuid}/submit`,
        data,
        {
          headers: {
            Authorization: `Bearer ${access_token}`,
          },
        },
      ),
    );
  }

  private invalidateDetailCachesAfter<T>(
    request: Observable<T>,
  ): Observable<T> {
    return request.pipe(
      tap(() => {
        this.proposalDetailsCache.clear();
        this.versionDetailsCache.clear();
        this.proposalBudgetDetailsCache.clear();
      }),
    );
  }
}
